/* ns-motion-home — home page motion. Nothing here hides content on its own:
   the CSS states are keyed to the data-nsh attributes written at runtime, so a
   script that never loads or throws leaves the page rendered. Revealed
   elements drop the attribute again, leaving no clip-path or transform. */
(function () {
  var root = document.documentElement;
  if (root.getAttribute('data-template') !== 'index') return;

  var NS = window.NSMotion || null;
  var reduced = NS ? NS.reduced : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) return;

  var EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var LEFT = '[data-nsh]:not(.nsh-in), [data-ns-motion]:not(.is-inview), [data-ns-motion-stagger]:not(.is-inview)';

  function q(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function show(el) {
    if (!el || el.classList.contains('nsh-in')) return;
    el.classList.add('nsh-in');
    setTimeout(function () {
      el.removeAttribute('data-nsh');
      el.style.removeProperty('--nsh-i');
      el.style.removeProperty('--nsh-dir');
    }, 2400);
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      show(e.target);
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });

  // A whole set revealed when an ancestor enters view — what a slider needs,
  // since its off-screen slides never intersect on their own.
  function watch(trigger, nodes) {
    var tio = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        tio.disconnect();
        nodes.forEach(show);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    tio.observe(trigger);
  }

  function mark(sel, kind, opts) {
    opts = opts || {};
    var nodes = q(sel);
    if (!nodes.length) return;
    var cap = opts.cap == null ? 5 : opts.cap;
    nodes.forEach(function (el, n) {
      el.setAttribute('data-nsh', kind);
      el.style.setProperty('--nsh-i', opts.index == null ? Math.min(n, cap) : opts.index);
    });
    if (!opts.trigger) {
      nodes.forEach(function (el) { io.observe(el); });
      return;
    }
    watch(nodes[0].closest(opts.trigger) || nodes[0].parentNode, nodes);
  }

  // Shared ns-motion-core primitives: same motion language as the other pages.
  function core(sel, value) {
    q(sel).forEach(function (el) {
      if (el.hasAttribute('data-ns-motion') || el.hasAttribute('data-ns-motion-stagger')) return;
      if (value === 'stagger') el.setAttribute('data-ns-motion-stagger', '');
      else el.setAttribute('data-ns-motion', value);
    });
  }

  // Hero headline, one line at a time. <split-lines> keeps its lines in a
  // shadow root, so they run on the Web Animations API. The reveal fires the
  // moment the element upgrades rather than after a frame poll; if that never
  // lands the host is un-held as one block at 1s, and if this file never runs
  // at all the stylesheet un-hides it by itself at 1.6s.
  function heroLines() {
    var host = document.querySelector('image-banner[reveal-on-scroll="false"] .title-fa split-lines');
    if (!host) return;
    var ran = false;

    function release() {
      host.style.animation = 'none';
      host.style.opacity = '1';
    }

    function start() {
      if (ran) return true;
      var lines = (host.lines || []).filter(function (l) { return l.firstElementChild; });
      if (!lines.length) return false;
      ran = true;
      release();
      lines.forEach(function (line, i) {
        line.style.clipPath = 'inset(0 0 -0.3em 0)';
        line.firstElementChild.animate(
          [{ transform: 'translateY(110%)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }],
          { duration: 600, delay: i * 70, easing: EASE, fill: 'both' }
        );
      });
      return true;
    }

    if (start()) return;
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('split-lines').then(start);
    }
    // The value proposition must not wait on a slow module.
    setTimeout(function () {
      if (ran) return;
      ran = true;
      release();
      host.animate(
        [{ transform: 'translateY(12px)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }],
        { duration: 450, easing: EASE, fill: 'both' }
      );
    }, 1000);
  }

  // The proof stat counts up the first time it is read. The real number is in
  // the DOM from the start: nothing here may ever leave the page claiming 0%,
  // so an interpolated value is written only once the count actually runs.
  function countUp() {
    var head = null;
    var digits = null;
    q('.media-with-text__content .prose p, .media-with-text__content .prose h2').some(function (el) {
      var m = (el.textContent || '').match(/(\d{1,3})%/);
      if (!m) return false;
      head = el;
      digits = m[1];
      return true;
    });
    if (!head) return;

    var walker = document.createTreeWalker(head, NodeFilter.SHOW_TEXT, null);
    var node = null;
    var t;
    while ((t = walker.nextNode())) {
      if (t.nodeValue.indexOf(digits + '%') !== -1) { node = t; break; }
    }
    if (!node) return;

    var target = parseInt(digits, 10);
    var tail = node.splitText(node.nodeValue.indexOf(digits + '%'));
    tail.splitText(digits.length);
    var span = document.createElement('span');
    span.className = 'nsh-count';
    span.textContent = digits;
    tail.parentNode.replaceChild(span, tail);
    // Pin the finished width so counting up can never reflow the sentence.
    span.style.minWidth = Math.ceil(span.getBoundingClientRect().width) + 'px';

    var done = false;
    function finish() {
      span.textContent = digits;
      span.classList.add('nsh-counted');
    }
    function play(animate) {
      if (done) return;
      done = true;
      cio.disconnect();
      if (!animate) return finish();
      var t0 = performance.now();
      requestAnimationFrame(function step(now) {
        var p = Math.min(1, (now - t0) / 1100);
        if (p < 1) {
          span.textContent = String(Math.round(target * (1 - Math.pow(1 - p, 4))));
          requestAnimationFrame(step);
        } else finish();
      });
    }

    var cio = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) play(true); });
    }, { threshold: 0.35 });
    cio.observe(head);

    // A stat the viewport has already scrolled clear of is finished, not left
    // counting. Strictly above the viewport, so a stat the shopper is looking
    // at right now still gets its count rather than a snap to the answer.
    rescues.push(function () {
      if (!done && head.getBoundingClientRect().bottom < 0) play(false);
      return done;
    });
  }

  // Hero and comparison CTAs take the shared hover fill from
  // ns-motion-journey.css instead of lifting. A button with no background
  // image is a flat colour, so it gets the solid fill; a gradient one keeps
  // the gradient default.
  function fills() {
    q('.fa-hero__cta .button, .nsct3__cta .button').forEach(function (btn) {
      if (btn.classList.contains('ns-j-fill')) return;
      btn.classList.add('ns-j-fill');
      if (getComputedStyle(btn).backgroundImage === 'none') btn.classList.add('ns-j-fill--solid');
    });
  }

  // Switching a tab crossfades the panel that appears instead of swapping it.
  function tabs() {
    q('.tabbed-media__tab').forEach(function (tab) {
      if (tab.__nshTab) return;
      tab.__nshTab = true;
      // The open tab re-clicked swaps nothing, so it gets no crossfade. The
      // section's inline handler flips the active class first, so the state is
      // read in capture, which runs before it.
      tab.addEventListener(
        'click',
        function () {
          if (tab.classList.contains('tabbed-media__tab--active')) return;
          setTimeout(function () {
            var wrap = tab.closest('.tabbed-media__inner') || document;
            var panel = wrap.querySelector('[data-tab-content-index="' + tab.getAttribute('data-tab-index') + '"]');
            if (!panel || !panel.animate) return;
            panel.animate(
              [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }],
              { duration: 450, easing: EASE }
            );
          }, 0);
        },
        true
      );
    });
  }

  // Feature columns sweep in sideways, alternating direction row by row, so the
  // three column sections read as their own move rather than a fourth rise.
  // Each row is triggered by its OWN container: at 390px the row is a snap
  // slider, and its off-screen items never intersect on their own.
  function columns() {
    q('.multi-column').forEach(function (row, r) {
      if (row.__nshCols) return;
      row.__nshCols = true;
      var items = q('.multi-column__item', row).filter(function (el) {
        return el.firstElementChild && !el.hasAttribute('data-nsh');
      });
      if (!items.length) return;
      items.forEach(function (el, n) {
        el.setAttribute('data-nsh', 'sweepx');
        el.style.setProperty('--nsh-i', Math.min(n, 4));
        el.style.setProperty('--nsh-dir', r % 2 ? -1 : 1);
      });
      watch(row, items);
    });
  }

  /* One focal entrance per section, and not the same entrance twice in a row.
     Section headers stay still wherever the body of their section moves, and
     the mission / closing / FAQ blocks fade with no travel at all — the
     stillness between sections is what makes the moving ones read. */
  function plan() {
    mark('product-card.ns-pc', 'card', { trigger: '.nsfc', cap: 4 });
    core('.nsfc-button--footer', 'fade');

    core('image-banner[reveal-on-scroll="true"] .icon-text-container', 'stagger');
    core('image-banner[reveal-on-scroll="true"] .fa-hero__cta', 'fade-up');

    // Labels sweep in from the left, then our own column fills upward.
    core('.nsct3__slider', 'fade');
    core('.nsct3__cta', 'fade-up');
    mark('.nsct3__col--labels .nsct3__cell', 'sweep', { trigger: '.nsct3__table', cap: 7 });
    mark('.nsct3__col--featured', 'fill', { trigger: '.nsct3__table', index: 2 });

    core('.nb-bullets__list', 'stagger');
    mark('.expert-slider .expert', 'rise', { trigger: '.expert-slider', cap: 3 });

    core('.review-section-with-filter__filters', 'stagger');
    mark('.review-section-with-filter__card-container-outer', 'rise', { index: 0 });
    core('.review-section-with-filter__footer', 'fade');

    core('.tabbed-media__bullets', 'stagger');

    columns();

    core('.rich-text__wrapper', 'fade');
    core('.faq-list', 'fade');

    if (NS) NS.observe(document);
  }

  function run(fn) {
    try { fn(); } catch (err) { q('[data-nsh]').forEach(show); }
  }

  // An IntersectionObserver only fires on a frame where the element really
  // intersects, so an anchor jump can skip one and leave it hidden for good.
  // This rAF-throttled sweep reveals whatever the viewport has already passed.
  var rescues = [];
  var ticking = false;
  function sweep() {
    var limit = window.innerHeight * 0.92;
    rescues = rescues.filter(function (fn) { return !fn(limit); });
    var left = q(LEFT);
    if (!left.length && !rescues.length) {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      return;
    }
    left.forEach(function (el) {
      if (el.getBoundingClientRect().bottom >= limit) return;
      if (el.hasAttribute('data-nsh')) show(el);
      else el.classList.add('is-inview');
    });
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { ticking = false; sweep(); });
  }

  /* On a slow connection the held states land after first paint, so a section
     that has already painted would TRANSITION out of view before coming back.
     .nsh-arming suppresses that for the frame in which they land. */
  root.classList.add('nsh-arming');
  run(function () { heroLines(); plan(); countUp(); tabs(); fills(); });
  requestAnimationFrame(function () {
    requestAnimationFrame(function () { root.classList.remove('nsh-arming'); });
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  document.addEventListener('shopify:section:load', function (e) {
    run(function () { plan(); tabs(); fills(); });
    if (e && e.target) q('[data-nsh]', e.target).forEach(show);
  });

  window.addEventListener('load', function () {
    setTimeout(function () {
      sweep();
      q('[data-nsh]').forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight) show(el);
      });
    }, 2500);
  });
})();
